import { bookingInquiryStrings } from '@data/bookingInquiry';
import { receivingWarehouseChineseHolidays } from '@data/bookingInquiryHolidays';
import { BookingInquiryForm, bookingInquiryForm } from '@models/BookingInquiry';
import { BookingInquiryDraft } from '@models/BookingInquiryDraft';
import { validateCustomsInstructionSpecialNote } from '@models/CustomsInstruction';
import { validateDeliveryInstructionSpecialNote } from '@models/DeliveryInstruction';
import { createValidate, formStrings, mergeErrors } from '@utils/forms';
import { fold } from 'fp-ts/lib/Either';
import { pipe } from 'fp-ts/lib/function';
import * as t from 'io-ts';
import * as h from 'tyrann-io';

const baseValidateBookingInquiryForm = createValidate(bookingInquiryForm);

export const makeValidateBookingInquiryForm = (stepName: string) => (value: any) => {
  const errors = baseValidateBookingInquiryForm(value);
  const { is_caution_checked } = value;

  // Validate is_caution_checked
  if (stepName === 'Confirmation') {
    pipe(
      bookingInquiryForm.props.is_caution_checked.decode(is_caution_checked),
      fold(
        () => {},
        (v) => {
          if (v.length === 0) {
            errors.is_caution_checked = bookingInquiryStrings.checkCautionsMessage;
          }
        },
      ),
    );
  }

  pipe(
    t
      .type({
        move_in_date: bookingInquiryForm.props.move_in_date,
        has_pickups: bookingInquiryForm.props.has_pickups,
        cargos_port_name: bookingInquiryForm.props.cargos_port_name,
        route_plan_simulator_result: bookingInquiryForm.props.route_plan_simulator_result,
      })
      .decode(value),
    fold(
      () => {},
      (v) => {
        if (!v.has_pickups) {
          if (!v.move_in_date?.trim().length) {
            errors.move_in_date = formStrings.notEmpty(bookingInquiryStrings.moveInDate);
          }

          if (v.move_in_date && stepName === 'InquiryForm') {
            const chineseHoliday = receivingWarehouseChineseHolidays.find(
              (h) => h.city === v.cargos_port_name,
            );

            if (
              chineseHoliday &&
              v.move_in_date >= chineseHoliday.begin &&
              v.move_in_date <= chineseHoliday.end
            ) {
              errors.move_in_date = bookingInquiryStrings.moveInDateHolidayError;
            }
          }
        }
      },
    ),
  );

  pipe(
    t
      .type({
        pickup_date: bookingInquiryForm.props.pickup_date,
        has_pickups: bookingInquiryForm.props.has_pickups,
        cargos2: bookingInquiryForm.props.cargos2,
      })
      .decode(value),
    fold(
      () => {},
      (v) => {
        if (v.has_pickups) {
          for (let i = 0; i < v.cargos2.length; i++) {
            if (!v.cargos2[i].pickup_address_index) {
              errors.cargos2 = {
                ...errors.cargos2,
                [i]: {
                  ...errors.cargo2?.[i],
                  pickup_address_index: bookingInquiryStrings.atLeastOnePickupAddress,
                },
              };
            }
          }

          if (!v.pickup_date?.trim().length) {
            errors.pickup_date = formStrings.notEmpty(bookingInquiryStrings.pickupDate);
          }
        }
      },
    ),
  );

  pipe(
    t
      .type({
        shipper: bookingInquiryForm.props.shipper,
        consignee: bookingInquiryForm.props.consignee,
        receiver: bookingInquiryForm.props.receiver,
        shipper_input: bookingInquiryForm.props.shipper_input,
        consignee_input: bookingInquiryForm.props.consignee_input,
        receiver_input: bookingInquiryForm.props.receiver_input,
      })
      .decode(value),
    fold(
      () => {},
      (v) => {
        if (!v.shipper && !v.shipper_input) {
          errors.shipper = formStrings.notEmpty(bookingInquiryStrings.shipper);
        }

        if (!v.consignee && !v.consignee_input) {
          errors.consignee = formStrings.notEmpty(bookingInquiryStrings.consignee);
        }

        if (!v.receiver && !v.receiver_input) {
          errors.receiver = formStrings.notEmpty(bookingInquiryStrings.receiver);
        }
      },
    ),
  );

  return mergeErrors(errors, validateBookingInquirySpecialNoteForm(value));
};

export const bookingInquirySpecialNoteForm = t.type({
  customs_instruction: h.omittable(bookingInquiryForm.props.customs_instruction),
  delivery_instruction: h.omittable(bookingInquiryForm.props.delivery_instruction),
  has_special_note: bookingInquiryForm.props.has_special_note,
  special_note: bookingInquiryForm.props.special_note,
});

export type BookingInquirySpecialNoteForm = t.TypeOf<typeof bookingInquirySpecialNoteForm>;

export const validateBookingInquirySpecialNoteForm = (value: any) => {
  const errors = createValidate(bookingInquirySpecialNoteForm)(value);

  return mergeErrors(errors, {
    customs_instruction:
      value.customs_instruction && validateCustomsInstructionSpecialNote(value.customs_instruction),
    delivery_instruction:
      value.delivery_instruction &&
      validateDeliveryInstructionSpecialNote(value.delivery_instruction),
  });
};

/**
 *
 * @param v
 * @returns if `route_plan_simulator_result` is invalid, discard it.
 */
export const mapBookingInquiryDraftToForm = (v: BookingInquiryDraft): BookingInquiryForm => {
  return pipe(
    t
      .type({
        route_plan_simulator_result: bookingInquiryForm.props.route_plan_simulator_result,
      })
      .decode(v.data),
    fold(
      () => ({ ...(v.data as any), route_plan_simulator_result: undefined }),
      () => v.data as any,
    ),
  );
};
